import { Injectable, inject } from '@angular/core';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { Observable, catchError, tap, throwError } from 'rxjs';
import { StateApiError, checkForBadHttpErrorMessage, checkToAddStateApiError, checkToRemoveStateApiError } from '@trovata/app/core/models/state-error.model';
import { StablecoinReportsService } from '../../../services/reports/reports.service';
import { StablecoinPage } from '../../../models/stablecoin.model';
import { StablecoinTransfer } from '../../../models/transfer.model';
import { StablecoinActivitySummaryRow, StablecoinReportDefinition } from '../../../models/report.model';
import {
	ClearStablecoinReportsState,
	CreateStablecoinReportDefinition,
	DeleteStablecoinReportDefinition,
	ExportStablecoinActivity,
	GetStablecoinActivity,
	GetStablecoinActivitySummary,
	GetStablecoinReportDefinitions,
	InitStablecoinReportsState,
	ResetStablecoinReportsState,
	UpdateStablecoinReportDefinition,
} from '../../actions/reports.actions';

export interface StablecoinReportsStateModel {
	reportDefinitions: StablecoinReportDefinition[] | null;
	activityItems: StablecoinTransfer[] | null;
	activityNextCursor: string | null;
	summaryRows: StablecoinActivitySummaryRow[] | null;
	apiErrors: StateApiError[];
	isCached: boolean;
	definitionsLoading: boolean;
	activityLoading: boolean;
	summaryLoading: boolean;
	exportLoading: boolean;
}

const DEFAULT_STABLECOIN_REPORTS_STATE: StablecoinReportsStateModel = {
	reportDefinitions: null,
	activityItems: null,
	activityNextCursor: null,
	summaryRows: null,
	apiErrors: [],
	isCached: false,
	definitionsLoading: true,
	activityLoading: true,
	summaryLoading: true,
	exportLoading: false,
};

@State<StablecoinReportsStateModel>({
	name: 'stablecoinReports',
	defaults: { ...DEFAULT_STABLECOIN_REPORTS_STATE },
})
@Injectable()
export class StablecoinReportsState {
	private readonly reportsService: StablecoinReportsService = inject(StablecoinReportsService);

	@Selector()
	static reportDefinitions(state: StablecoinReportsStateModel): StablecoinReportDefinition[] | null {
		return state.reportDefinitions;
	}

	@Selector()
	static activityItems(state: StablecoinReportsStateModel): StablecoinTransfer[] | null {
		return state.activityItems;
	}

	@Selector()
	static activityNextCursor(state: StablecoinReportsStateModel): string | null {
		return state.activityNextCursor;
	}

	@Selector()
	static summaryRows(state: StablecoinReportsStateModel): StablecoinActivitySummaryRow[] | null {
		return state.summaryRows;
	}

	@Selector()
	static definitionsLoading(state: StablecoinReportsStateModel): boolean {
		return state.definitionsLoading;
	}

	@Selector()
	static activityLoading(state: StablecoinReportsStateModel): boolean {
		return state.activityLoading;
	}

	@Selector()
	static summaryLoading(state: StablecoinReportsStateModel): boolean {
		return state.summaryLoading;
	}

	@Selector()
	static exportLoading(state: StablecoinReportsStateModel): boolean {
		return state.exportLoading;
	}

	@Selector()
	static apiErrors(state: StablecoinReportsStateModel): StateApiError[] {
		return state.apiErrors;
	}

	@Action(InitStablecoinReportsState)
	init(): void {
		// Fetches are dispatched lazily from page components (no entitlement gate yet).
	}

	@Action(ClearStablecoinReportsState)
	clear(context: StateContext<StablecoinReportsStateModel>): void {
		context.setState({ ...DEFAULT_STABLECOIN_REPORTS_STATE });
	}

	@Action(ResetStablecoinReportsState)
	reset(context: StateContext<StablecoinReportsStateModel>): void {
		context.setState({ ...DEFAULT_STABLECOIN_REPORTS_STATE });
	}

	@Action(GetStablecoinReportDefinitions)
	getReportDefinitions(context: StateContext<StablecoinReportsStateModel>): Observable<StablecoinReportDefinition[]> {
		context.patchState({ definitionsLoading: true });
		return this.reportsService.getReportDefinitions().pipe(
			tap((reportDefinitions: StablecoinReportDefinition[]) => {
				checkToRemoveStateApiError(context, GetStablecoinReportDefinitions);
				context.patchState({ reportDefinitions, definitionsLoading: false, isCached: true });
			}),
			catchError((error: HttpErrorResponse) => {
				context.patchState({ definitionsLoading: false });
				checkToAddStateApiError(error, context, GetStablecoinReportDefinitions, checkForBadHttpErrorMessage(error, 'Unable to load saved reports.').message);
				return throwError(() => error);
			})
		);
	}

	@Action(CreateStablecoinReportDefinition)
	createReportDefinition(context: StateContext<StablecoinReportsStateModel>, action: CreateStablecoinReportDefinition): Observable<StablecoinReportDefinition> {
		return this.reportsService.createReportDefinition(action.body).pipe(
			tap((definition: StablecoinReportDefinition) => {
				checkToRemoveStateApiError(context, CreateStablecoinReportDefinition);
				context.patchState({ reportDefinitions: [definition, ...(context.getState().reportDefinitions ?? [])] });
			}),
			catchError((error: HttpErrorResponse) => {
				checkToAddStateApiError(error, context, CreateStablecoinReportDefinition, checkForBadHttpErrorMessage(error, 'Unable to save report.').message);
				return throwError(() => error);
			})
		);
	}

	@Action(UpdateStablecoinReportDefinition)
	updateReportDefinition(context: StateContext<StablecoinReportsStateModel>, action: UpdateStablecoinReportDefinition): Observable<StablecoinReportDefinition> {
		return this.reportsService.updateReportDefinition(action.reportId, action.body).pipe(
			tap((definition: StablecoinReportDefinition) => {
				checkToRemoveStateApiError(context, UpdateStablecoinReportDefinition);
				context.patchState({
					reportDefinitions:
						context.getState().reportDefinitions?.map((item: StablecoinReportDefinition) => (item.id === definition.id ? definition : item)) ?? null,
				});
			}),
			catchError((error: HttpErrorResponse) => {
				checkToAddStateApiError(error, context, UpdateStablecoinReportDefinition, checkForBadHttpErrorMessage(error, 'Unable to update report.').message);
				return throwError(() => error);
			})
		);
	}

	@Action(DeleteStablecoinReportDefinition)
	deleteReportDefinition(context: StateContext<StablecoinReportsStateModel>, action: DeleteStablecoinReportDefinition): Observable<void> {
		return this.reportsService.deleteReportDefinition(action.reportId).pipe(
			tap(() => {
				checkToRemoveStateApiError(context, DeleteStablecoinReportDefinition);
				context.patchState({
					reportDefinitions: context.getState().reportDefinitions?.filter((item: StablecoinReportDefinition) => item.id !== action.reportId) ?? null,
				});
			}),
			catchError((error: HttpErrorResponse) => {
				checkToAddStateApiError(error, context, DeleteStablecoinReportDefinition, checkForBadHttpErrorMessage(error, 'Unable to delete report.').message);
				return throwError(() => error);
			})
		);
	}

	@Action(GetStablecoinActivity)
	getActivity(context: StateContext<StablecoinReportsStateModel>, action: GetStablecoinActivity): Observable<StablecoinPage<StablecoinTransfer>> {
		context.patchState({ activityLoading: true });
		return this.reportsService.getActivity(action.filter, action.cursor).pipe(
			tap((page: StablecoinPage<StablecoinTransfer>) => {
				checkToRemoveStateApiError(context, GetStablecoinActivity);
				const state: StablecoinReportsStateModel = context.getState();
				const activityItems: StablecoinTransfer[] = action.cursor ? [...(state.activityItems ?? []), ...page.items] : page.items;
				context.patchState({ activityItems, activityNextCursor: page.next_cursor, activityLoading: false });
			}),
			catchError((error: HttpErrorResponse) => {
				context.patchState({ activityLoading: false });
				checkToAddStateApiError(error, context, GetStablecoinActivity, checkForBadHttpErrorMessage(error, 'Unable to load activity.').message);
				return throwError(() => error);
			})
		);
	}

	@Action(GetStablecoinActivitySummary)
	getActivitySummary(context: StateContext<StablecoinReportsStateModel>, action: GetStablecoinActivitySummary): Observable<StablecoinActivitySummaryRow[]> {
		context.patchState({ summaryLoading: true });
		return this.reportsService.getActivitySummary(action.groupBy, action.filter).pipe(
			tap((summaryRows: StablecoinActivitySummaryRow[]) => {
				checkToRemoveStateApiError(context, GetStablecoinActivitySummary);
				context.patchState({ summaryRows, summaryLoading: false });
			}),
			catchError((error: HttpErrorResponse) => {
				context.patchState({ summaryLoading: false });
				checkToAddStateApiError(error, context, GetStablecoinActivitySummary, checkForBadHttpErrorMessage(error, 'Unable to load report summary.').message);
				return throwError(() => error);
			})
		);
	}

	@Action(ExportStablecoinActivity)
	exportActivity(context: StateContext<StablecoinReportsStateModel>, action: ExportStablecoinActivity): Observable<HttpResponse<Blob>> {
		context.patchState({ exportLoading: true });
		return this.reportsService.exportActivity(action.filter).pipe(
			tap((response: HttpResponse<Blob>) => {
				checkToRemoveStateApiError(context, ExportStablecoinActivity);
				context.patchState({ exportLoading: false });
				this.downloadBlob(response);
			}),
			catchError((error: HttpErrorResponse) => {
				context.patchState({ exportLoading: false });
				checkToAddStateApiError(error, context, ExportStablecoinActivity, checkForBadHttpErrorMessage(error, 'Unable to export CSV.').message);
				return throwError(() => error);
			})
		);
	}

	private downloadBlob(response: HttpResponse<Blob>): void {
		const contentDisposition: string = response.headers.get('Content-Disposition') ?? '';
		const filenameMatch: RegExpMatchArray | null = contentDisposition.match(/filename="?([^";]+)"?/);
		const filename: string = filenameMatch?.[1] ?? 'stablecoin-activity.csv';
		const url: string = URL.createObjectURL(response.body ?? new Blob());
		const anchor: HTMLAnchorElement = document.createElement('a');
		anchor.href = url;
		anchor.download = filename;
		anchor.click();
		URL.revokeObjectURL(url);
	}
}
